import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectShowDeferredPaywall, setShowDeferredPaywallCreator } from '~/redux/slices/modals'
import ROUTES from '~/shared/constants/routes.const'

// Marks the regions where a click raises the deferred paywall: the page content wrapper
// (DefaultLayout), the footer and the bottom navigation. Clicks anywhere else — top header,
// cookie-consent dialog, other overlays — behave normally.
export const PAYWALL_TRIGGER_ATTRIBUTE = 'data-paywall-trigger'

const PAYWALL_TRIGGER_SELECTOR = `[${PAYWALL_TRIGGER_ATTRIBUTE}]`

// Public, indexable sections. `router.asPath` is locale-stripped, so bare prefixes match all locales.
const DEFERRED_PAYWALL_ROUTES = [ROUTES.ENCYCLOPEDIA, ROUTES.DRILL]

/**
 * Defers the paywall on public content pages until the user interacts: the first click on a
 * `PAYWALL_TRIGGER_ATTRIBUTE` region is swallowed and raises the paywall instead. Resets on
 * navigation, so each page is previewable again.
 *
 * @param isApplicable Whether the paywall applies to the current user; when false, no listener attaches.
 */
export default function useDeferredPaywallTrigger({ isApplicable }: { isApplicable: boolean }): boolean {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const isTriggered = useAppSelector(selectShowDeferredPaywall)
  const isDeferredPaywallPath = DEFERRED_PAYWALL_ROUTES.some((route) => router.asPath.startsWith(route))

  // Reset on navigation so a freshly opened page is readable before interaction.
  useEffect(() => {
    dispatch(setShowDeferredPaywallCreator(false))
  }, [router.asPath, dispatch])

  useEffect(() => {
    if (!isDeferredPaywallPath || !isApplicable || isTriggered) return

    const handleClick = (event: MouseEvent): void => {
      const target = event.target as Element | null
      if (!target?.closest(PAYWALL_TRIGGER_SELECTOR)) return
      // Swallow the triggering click: otherwise a link under it would navigate, and the
      // reset-on-navigation effect would dismiss the paywall right away. The capture phase
      // runs before React's own handlers, so buttons under the click don't fire either.
      event.preventDefault()
      event.stopPropagation()
      dispatch(setShowDeferredPaywallCreator(true))
    }

    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [isDeferredPaywallPath, isApplicable, isTriggered, dispatch])

  return isDeferredPaywallPath && isTriggered
}
